import type { IndustryPage } from "@/lib/data";

export const builders: IndustryPage = {
  slug: "builders",
  seoTitle:
    "Builders Website Design UK | Fixed £1,000, You Own It",
  seoDescription:
    "Builders website design with project galleries, extension and loft pages, FMB or TrustMark badges you hold. Fixed £1,000, live in 7 days. Book a call.",
  serviceName: "Website design for builders",
  tldr:
    "Builders website design from ConstructionWebDesign.uk gives UK builders a mobile-first site with a project gallery, pages for extensions, loft conversions and renovations, a quote form and the FMB or TrustMark badges you hold. The price is £1,000, one-off and fixed, with no subscription. Your website goes live in 7 days from the discovery call, and you own it.",
  pricingFromGBP: 1000,
  hero: {
    eyebrow: "Builders website design · UK",
    headline: "Website design for builders, built around your finished projects.",
    highlight: "your finished projects",
    sub: "Websites for UK builders with a project gallery, a page for each service such as extensions and loft conversions, a one-tap call button, a quote form and the FMB or TrustMark badges you hold.",
    secondaryCta: { label: "See builder demos", href: "#demos" },
  },
  slides: [
    {
      bg: "https://constructionwebdesign.cdn.modulify.website/images/hero/extension_builder_slider_ratio.jpg",
      alt: "Builder's site team forming a steel-and-block rear extension to a UK home",
      headlineTop: "Build your construction",
      headlineBottom: "company, fast.",
      preview: "https://constructionwebdesign.cdn.modulify.website/images/demo-fullheight/constructum.jpg",
    },
  ],
  demoSlugs: ["constructum", "coreframe", "stackline", "northcrest", "halden"],
  demosHeading: "Builders website design demo templates.",
  demosSub:
    "Demo templates for builders, not client work. Open one to see the layout, project gallery and copy style for extensions, loft conversions and renovations.",
  value: {
    eyebrow: "Why it matters",
    heading: "Why does a builder need a website?",
    intro:
      "A builder needs a website because homeowners planning an extension, loft conversion or renovation want to see your past work before they invite you to quote. A website shows your finished projects, the accreditations you hold and your reviews, and takes enquiries while you are on site.",
    points: [
      {
        title: "Show the size and quality of your work.",
        body: "A homeowner spending serious money on an extension wants to see what you have built before. Photos of finished extensions, loft conversions and renovations, with the scope of each job, show the standard of your work better than a list of services.",
        icon: "Buildings",
      },
      {
        title: "Turn every finished build into a project page.",
        body: "Before, during and after photos for each recent job, each on its own page with the town, the scope and the trades involved. Every project page is proof of work for a homeowner and another page Google can show for searches about that type of build.",
        icon: "Sparkle",
      },
      {
        title: "Built for \"builders near me\" searches.",
        body: "Builders web design from ConstructionWebDesign.uk includes on-page local SEO that names the towns and areas you work in across your service and project pages. When a homeowner nearby searches for a builder, the site gives Google clear information about what you build and where.",
        icon: "Pin",
      },
      {
        title: "Show the accreditations you hold.",
        body: "FMB (Federation of Master Builders) membership, TrustMark, NHBC registration and CSCS cards answer the questions a homeowner asks before signing a contract. The site shows the ones you hold, with registration numbers linked to the official register, and leaves off any you don't.",
        icon: "Shield",
      },
    ],
  },
  features: {
    eyebrow: "What's in the site",
    heading: "What does website design for builders include?",
    intro:
      "Website design for builders from ConstructionWebDesign.uk includes a project gallery with a page per build, a page for each service, a one-tap call button, a quote form and the accreditations you hold. Every page is mobile-first, with on-page local SEO for the areas you work in.",
    items: [
      {
        title: "A project gallery with one page per build.",
        body: "Extensions, loft conversions, renovations, refurbishments, new builds and groundworks each get their own project pages, with before, during and after photos, the town, the scope of work and how long the job took. A homeowner planning similar work can see a finished job like theirs.",
      },
      {
        title: "A page for every service.",
        body: "Extensions, loft conversions, renovations and refurbishments, new builds, design and build, and project management each get a dedicated page. Each one explains what the work involves, what you handle, such as arranging drawings, Building Regulations approval or party wall notices where you offer it, and the areas you cover.",
      },
      {
        title: "One-tap call and a sticky number bar.",
        body: "On mobile, your phone number is the first thing a visitor sees, not a slideshow. The one-tap call button stays on screen while they scroll through your projects, so ringing you is always one tap away.",
      },
      {
        title: "Price guides and a quote form.",
        body: "If you want to publish them, the site can show your own typical price ranges for common jobs such as a single-storey rear extension, a loft conversion or a full refurbishment. The quote form asks for the postcode, the scope of work and a budget band, so you can spot the jobs worth pricing.",
      },
      {
        title: "FMB, TrustMark and NHBC badges, if you hold them.",
        body: "If your firm holds FMB membership, TrustMark registration (the government-endorsed quality scheme for trades), NHBC registration, Constructionline, CHAS or CSCS cards, the badges sit near the top of the page with registration numbers linked to the official register. If you don't hold a scheme, its badge stays off the site.",
      },
      {
        title: "Mobile-first pages with on-page local SEO.",
        body: "Every page is designed for a phone first. Page titles, headings and copy name your services and the towns you cover, and area pages for the places you work give Google clear information about where you build.",
      },
    ],
  },
  faqs: [
    {
      q: "How much does a website for a builder cost in the UK?",
      a: "A website for a builder from ConstructionWebDesign.uk costs £1,000, a one-off fixed price with no subscription. The Website + Brand package costs £1,399 and adds a logo, visual identity and business card mockups. Typical UK ranges are about £4,000–£5,000 for a freelancer, £5,000–£25,000 for an agency and about £22 a month for Wix or Squarespace. The builder owns the finished website.",
    },
    {
      q: "How long does it take to build a website for a builder?",
      a: "A builder's website from ConstructionWebDesign.uk goes live in 7 days from the discovery call. The five steps are a discovery call, strategy and sitemap, design, build, then launch and support. Gathering photos of finished jobs, your accreditation numbers and the list of areas you cover before the call keeps the week on schedule.",
    },
    {
      q: "What should a builder's website include?",
      a: "A builder's website should include a project gallery with before and after photos, a page for each service (extensions, loft conversions, renovations, new builds), a one-tap call button, a quote form, customer reviews, the areas you cover and the accreditations you hold, such as FMB or TrustMark. Each page should load quickly and read well on a phone.",
    },
    {
      q: "Do builders need a website?",
      a: "Yes, builders need a website, even if most of your work comes through word of mouth. A homeowner who hears your name from a neighbour is likely to look you up before inviting you to quote, and a website is where they can see finished extensions and lofts, your FMB or TrustMark membership if you hold it, and your reviews.",
    },
    {
      q: "Does web design for builders include SEO?",
      a: "Yes, web design for builders from ConstructionWebDesign.uk includes on-page local SEO as standard in the £1,000 package. Page titles, headings and copy name your services and the towns you cover, and each service and project gets its own page. Nobody can honestly guarantee a Google ranking, but that setup, plus a complete Google Business Profile and regular reviews, gives a builder a solid base for local search.",
    },
    {
      q: "Should a builder put prices on their website?",
      a: "Yes, a builder can show typical price ranges on their website, as long as they are labelled as a guide rather than a fixed quote. On a website from ConstructionWebDesign.uk, price guides for jobs such as a single-storey rear extension or a loft conversion are optional and use your own figures. A price guide helps filter out enquiries that don't fit the jobs you want, and the budget band on the quote form does the same.",
    },
    {
      q: "Can a builder's website show FMB and TrustMark membership?",
      a: "Yes, if your firm holds FMB (Federation of Master Builders) membership or TrustMark registration, a ConstructionWebDesign.uk website shows the badge near the top of the page with your registration number linked to the official register. The same goes for NHBC registration, CHAS, Constructionline and CSCS cards. Only schemes you actually hold are shown, because a badge you don't hold would mislead customers.",
    },
    {
      q: "Can a one-man-band builder get a website?",
      a: "Yes, a sole trader or one-man-band builder gets the same £1,000 fixed-price website from ConstructionWebDesign.uk as a larger firm. The site is planned around the work you do and the jobs you want more of, so a smaller firm might have fewer service pages and a simpler about page, with the same project gallery, quote form and on-page local SEO.",
    },
  ],
  guides: [
    {
      href: "/blog/how-much-should-a-construction-website-cost",
      label: "How much does a construction website cost in the UK?",
    },
    {
      href: "/blog/local-seo-for-builders",
      label: "Local SEO for builders: rank for \"builder near me\"",
    },
    {
      href: "/blog/google-business-profile-for-builders",
      label: "Google Business Profile for builders: setup guide",
    },
    {
      href: "/blog/how-to-price-building-work",
      label: "How to price building work without losing money",
    },
  ],
  finalCta: {
    headline: "Put your finished builds online.",
    highlight: "your finished builds",
    sub: "Book a free 30-minute call to talk through the projects you want more of. Fixed £1,000 price, and your website goes live in 7 days.",
  },
};
